'use client'

import { useMemo, useContext, useState, useEffect } from 'react'
import { Loader2 } from 'lucide-react'
import Danger from '@/app/manager/settings/sections/Danger'
import Members from '@/app/manager/settings/sections/Members'
import Groups from '@/app/manager/settings/sections/Groups'
import Roles from '@/app/manager/settings/sections/Roles'
import { AuthContext } from '@/contexts/AuthContext'
import { hasSomeAccess } from '@/lib/utils/common'
import NoAccess from '@/components/common/feedback/NoAccess'
import Organization from '@/app/manager/settings/sections/Organization'
import AssumptionFreshnessPanel from '@/app/manager/settings/sections/AssumptionFreshnessPanel'
import InvalidationRulesPanel from '@/app/manager/settings/sections/InvalidationRulesPanel'
import ParameterRegistry from '@/app/manager/settings/sections/ParameterRegistry'
import RevalidationClosurePanel from '@/app/manager/settings/sections/RevalidationClosurePanel'
import RevalidationPolicyPanel from '@/app/manager/settings/sections/RevalidationPolicyPanel'
import DriftRoutingPolicyCard from '@/app/manager/settings/sections/DriftRoutingPolicyCard'
import NotificationSettingsCard from '@/app/manager/settings/sections/NotificationSettingsCard'
import NotificationPreferencesCard from '@/app/manager/settings/sections/NotificationPreferencesCard'
import Simulations from '@/app/manager/settings/sections/Simulations'
import AIProvider from '@/app/manager/settings/sections/AIProvider'
import AdminActions from '@/app/manager/settings/sections/AdminActions'
import EventRecording from '@/app/manager/settings/sections/EventRecording'
import Billing from '@/app/manager/settings/sections/Billing'
import Teams from '@/app/manager/settings/sections/Teams'
import { useSearchParams } from 'next/navigation'
import { getBillingDetails } from '@/lib/utils/billing'
import Usage from '@/app/manager/settings/sections/Usage'
import Integrations from '@/app/manager/settings/sections/Integrations'
import { SidebarTrigger } from '@/components/ui/shadcn/sidebar'
import { Separator } from '@/components/ui/shadcn/separator'
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/components/ui/shadcn/tabs'

export default function Settings() {
  const authContext = useContext(AuthContext)
  const [tab, setTab] = useState('organization')
  const [billingDetails, setBillingDetails] = useState(null)
  const searchParams = useSearchParams()

  // Check administrative permissions
  const canManageUsers = authContext.user?.isAuthorized('userManagement')
  const canManageOrganization = authContext.user?.isAuthorized(
    'organizationSettings',
  )
  const canViewTeams = authContext.user?.isAuthorized('viewTeams')
  const canAccessBilling = authContext.user?.isAuthorized('billingAccess')
  const canManageEventRecording = authContext.user?.isAuthorized(
    'manageOrgEventRecording',
  )
  const canViewCriticalDependencies = authContext.user?.isAuthorized(
    'viewCriticalDependencies',
  )

  const showNoAccess = useMemo(() => {
    return !hasSomeAccess(authContext.user.RBACProfile?.permissions)
  }, [authContext.user.RBACProfile?.permissions])

  useEffect(() => {
    const paramTab = searchParams.get('tab')
    if (paramTab) setTab(paramTab)

    // Get the billing details
    getBillingDetails(authContext).then(b => setBillingDetails(b))
  }, [searchParams])

  if (!billingDetails) {
    return (
      <div className="vertically-centered">
        <Loader2 className="size-6 animate-spin" />
      </div>
    )
  }

  return (
    <>
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-border px-4">
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="!h-4" />
        <h1 className="text-sm font-medium">Settings</h1>
      </header>
      <div className="flex flex-1 flex-col p-4 gap-4">
        {showNoAccess && <NoAccess />}
        {!showNoAccess && (
          <Tabs
            value={tab}
            onValueChange={setTab}
            className="flex flex-1 flex-col gap-4"
          >
            <TabsList>
              {canManageOrganization && (
                <TabsTrigger value="organization">Organization</TabsTrigger>
              )}
              {canViewCriticalDependencies && (
                <TabsTrigger value="critical-dependencies">
                  Critical Dependencies
                </TabsTrigger>
              )}
              {canManageUsers && (
                <TabsTrigger value="members">Members</TabsTrigger>
              )}
              {canManageUsers && (
                <TabsTrigger value="access">Access</TabsTrigger>
              )}
              {canViewTeams && <TabsTrigger value="teams">Teams</TabsTrigger>}
              {canManageOrganization && (
                <TabsTrigger value="integrations">Integrations</TabsTrigger>
              )}
              {canAccessBilling && billingDetails?.tier !== 'Enterprise' && (
                <TabsTrigger value="billing">Billing</TabsTrigger>
              )}
              {canAccessBilling && billingDetails?.tier === 'Enterprise' && (
                <TabsTrigger value="usage">Usage</TabsTrigger>
              )}
              <TabsTrigger value="notifications">Notifications</TabsTrigger>
            </TabsList>

            {canManageOrganization && (
              <TabsContent value="organization" className="flex flex-col gap-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-4">
                    <Organization />
                    <AssumptionFreshnessPanel />
                    <AIProvider />
                    <AdminActions />
                  </div>
                  <div className="flex flex-col gap-4">
                    <Simulations />
                    {canManageEventRecording && <EventRecording />}
                  </div>
                </div>
                <InvalidationRulesPanel />
                <RevalidationClosurePanel />
                <RevalidationPolicyPanel />
                <DriftRoutingPolicyCard />
                <Danger />
              </TabsContent>
            )}
            {canViewCriticalDependencies && (
              <TabsContent
                value="critical-dependencies"
                className="flex flex-1 flex-col"
              >
                <div className="rounded-lg border bg-card p-4 flex-1 flex flex-col">
                  <ParameterRegistry />
                </div>
              </TabsContent>
            )}
            {canManageUsers && (
              <TabsContent value="members">
                <Members billingDetails={billingDetails} />
              </TabsContent>
            )}
            {canManageUsers && billingDetails?.teamSize > 1 && (
              <TabsContent value="access" className="flex flex-col gap-4">
                <Groups />
                <Roles />
              </TabsContent>
            )}
            {canViewTeams && (
              <TabsContent value="teams" className="flex flex-1">
                <div className="rounded-lg border bg-card p-4 flex-1 flex flex-col">
                  <Teams />
                </div>
              </TabsContent>
            )}
            {canManageOrganization && (
              <TabsContent value="integrations">
                <Integrations />
              </TabsContent>
            )}
            {canAccessBilling && (
              <TabsContent value="billing">
                <Billing />
              </TabsContent>
            )}
            {canAccessBilling && (
              <TabsContent value="usage">
                <Usage billingDetails={billingDetails} />
              </TabsContent>
            )}
            <TabsContent value="notifications" className="flex flex-col gap-4">
              {canManageOrganization && <NotificationSettingsCard />}
              <NotificationPreferencesCard />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </>
  )
}
